import { useEffect, useId, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../../components/FeatherIcon'
import { ErrorLine, Loading, SelectField } from '../../settings/kit'
import { useLoad } from '../../settings/kitHooks'
import type { StreamingCtx } from './context'
import { DASHBOARD_REFERER, OBS_DOWNLOAD_URL, obsServers, type ObsServerId } from './streamingData'

/**
 * "OBS software settings" from OnlyFans' Settings > Streaming (users/me/settings):
 * the server and stream key to paste into OBS, read-only with copy buttons.
 * Resetting the key stays in Settings > Streaming. The key itself lets anyone
 * stream as the creator, so only those who may change this page see it.
 * Folded until opened (the dashboard itself has no such block on OnlyFans).
 */
export function ObsCard({ ctx }: { ctx: StreamingCtx }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'streamingPage' })
  const [open, setOpen] = useState(false)
  return (
    <section className={`ofst-obs ${open ? 'is-open' : ''}`} aria-label={t('obs.title')}>
      <button type="button" className="oset-row oset-link oset-group-title ofst-obs-toggle" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className="oset-row-text">{t('obs.title')}</span>
        <FeatherIcon name={open ? 'chevron-up' : 'chevron-down'} size={24} className="oset-chevron" />
      </button>
      {open && <ObsSettings ctx={ctx} />}
    </section>
  )
}

function ObsSettings({ ctx }: { ctx: StreamingCtx }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'streamingPage' })
  const settings = useLoad(async () => obsServers(await ctx.of.get('users/me/settings', {}, DASHBOARD_REFERER)), [ctx.of])
  const [selected, setSelected] = useState<ObsServerId>('obs_agora')

  let body
  if (settings.data) {
    const s = settings.data.find((x) => x.id === selected) ?? settings.data[0]!
    body = (
      <>
        <SelectField
          label={t('obs.server')}
          value={selected}
          options={[
            { value: 'obs_agora', label: t('obs.cloudbeta') },
            { value: 'mux_direct', label: t('obs.alternative') },
          ]}
          onChange={setSelected}
        />
        <ReadOnlyField label={t('obs.serverField')} value={s.server} copy={t('obs.copy')} />
        {ctx.canWrite ? (
          s.key ? <ReadOnlyField label={t('obs.key')} value={s.key} copy={t('obs.copyKey')} /> : null
        ) : (
          <p className="oset-note ofst-obs-hidden">{t('obs.keyHidden')}</p>
        )}
      </>
    )
  } else if (settings.loading) {
    body = <Loading />
  } else {
    body = <ErrorLine onRetry={settings.reload}>{t('loadFailed', { error: settings.error || '—' })}</ErrorLine>
  }

  return (
    <div className="ofst-obs-body">
      <p className="ofst-obs-intro">
        <Trans t={t} i18nKey="obs.intro" components={[<a key="obs" href={OBS_DOWNLOAD_URL} target="_blank" rel="nofollow noopener noreferrer" />]} />
      </p>
      {body}
    </div>
  )
}

/** OnlyFans' readonly InputTextField with its CopyButton under it. */
function ReadOnlyField({ label, value, copy }: { label: string; value: string; copy: string }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'streamingPage' })
  const id = useId()
  const [copied, setCopied] = useState(false)
  const timer = useRef<number | undefined>(undefined)
  useEffect(() => () => window.clearTimeout(timer.current), [])
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      window.clearTimeout(timer.current)
      timer.current = window.setTimeout(() => setCopied(false), 2000)
    } catch {
      /* clipboard refused: the value stays selectable */
    }
  }
  return (
    <div className="ofst-ro">
      <div className="oset-field">
        <div className="oset-field-box is-floated">
          <label htmlFor={id} className="oset-field-label">
            {label}
          </label>
          <input id={id} className="oset-field-input" value={value} readOnly onFocus={(e) => e.currentTarget.select()} />
        </div>
      </div>
      <div className="ofst-ro-btns">
        <button type="button" className="oset-btn oset-btn--flat oset-btn--sm" disabled={!value} onClick={() => void doCopy()}>
          {copied ? t('obs.copied') : copy}
        </button>
      </div>
    </div>
  )
}
